import {
  ComponentTemplateDto,
  ComponentType,
  LibraryPrivacy,
} from "@growth-machine-llc/stridist-api-client";
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  TableCell,
  TableRow,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import React from "react";
import { ComponentTemplateMenu } from "./ComponentTemplateMenu";
import { ComponentIcon } from "../program-component/ComponentIcon";
import { ArrowForwardRounded } from "@mui/icons-material";
import { useCurrentWorkspace } from "../../hooks/useCurrentWorkspace";
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from "material-ui-popup-state/hooks";
import { SelectComponentIconsMenu } from "../menu/SelectComponentIconsMenu";
import { getColorByComponentType } from "../program-calendar/utils/common";
import { ChangeComponentCoverDialog } from "../dialog/ChangeComponentCoverDialog";
import { useUpdateLibraryComponentTemplate } from "./hooks/useUpdateLibraryComponentTemplate";
import logo from "../../components/app/logo.svg";
import { clsx } from "clsx";
import { JourneyAnchors, TeamRole } from "../../constants";
import { generateThumbnailUrl } from "../../utils/thumbnail";
import { getComponentTemplateLibraryWidths } from "./ComponentTemplateLibrary";
import { ComponentTemplatePrivacyBadge } from "./ComponentTemplatePrivacyBadge";
import { ComponentTemplateCategoryBadge } from "./ComponentTemplateCategoryBadge";

type StyleProps = {
  image: string;
  selected: boolean;
  isComponentImage: boolean;
  isPrivate?: boolean;
  borderColor: string;
};

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  root: {
    backgroundColor: ({ selected }) =>
      selected ? theme.palette.selected.main : "inherit",
  },
  cover: {
    backgroundImage: ({ image }) => `url("${image}")`,
    backgroundSize: ({ isComponentImage }) =>
      isComponentImage ? "cover" : "contain",
    backgroundColor: ({ isComponentImage }) =>
      isComponentImage ? "inherit" : theme.palette.background.default,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    width: theme.spacing(20),
    height: theme.spacing(10.75),
    borderRadius: theme.spacing(1.5),
    border: ({ borderColor, selected }) =>
      selected && `1px solid ${borderColor}`,
    filter: "drop-shadow(0px 5px 4px rgba(0, 0, 0, 0.05))",
    cursor: ({ isPrivate }) => (isPrivate ? "pointer" : "default"),
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: theme.spacing(-0.5),
    textOverflow: "ellipsis",
    overflow: "hidden",

    [theme.breakpoints.up("md")]: {
      marginRight: theme.spacing(10),
    },
  },
  wrapper: {
    display: "flex",
    padding: theme.spacing(1, 0),
    width: "100%",
  },
  contentContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: theme.spacing(1),
    justifyContent: "space-between",
    cursor: "pointer",
    flex: 1,
    border: ({ selected, borderColor }) =>
      `1px solid ${selected ? borderColor : theme.palette.border.primary}`,
    borderRadius: theme.spacing(1),
  },
  icon: {
    padding: theme.spacing(1),
    margin: theme.spacing(0, 1, 0, -1),
    cursor: "pointer",
    borderRadius: theme.spacing(1),
    "&:hover": {
      backgroundColor: ({ isPrivate }) =>
        isPrivate && theme.palette.selected.main,
    },
  },
  iconsMenu: {
    width: theme.spacing(40),
    zIndex: "9999 !important" as any,
  },
}));

export interface ComponentTemplateRowProps {
  component: ComponentTemplateDto;
  selected?: boolean;
  type: ComponentType;
  onContentClick?: (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => void;
  currentUser: {
    id: number;
    isTeamAccount: boolean;
    teamRole: TeamRole;
  };
  onContentDoubleClick?: (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => void;

  onEdit?: () => void;
  className?: string;
}

const ComponentTemplateRow = (props: ComponentTemplateRowProps) => {
  const {
    component,
    selected,
    type,
    onEdit,
    onContentClick,
    onContentDoubleClick,
    className,
    currentUser,
  } = props;
  const borderColor = getColorByComponentType(type as ComponentType);

  const columnWidths = getComponentTemplateLibraryWidths();

  const workspace = useCurrentWorkspace();
  const defaultLogo =
    generateThumbnailUrl({
      src: workspace?.logoUrl || logo,
      applyModifiers: true,
    }) || logo;

  const componentImage = component.image
    ? generateThumbnailUrl({
        src: component.image,
        applyModifiers: true,
      })
    : null;

  const isPrivate = currentUser.id === component.coachId;

  const s = useStyles({
    image: componentImage || defaultLogo,
    selected: selected || false,
    isComponentImage: Boolean(componentImage),
    isPrivate: isPrivate,
    borderColor,
  });

  const iconsMenuState = usePopupState({
    variant: "popover",
    popupId: "iconsMenu",
  });

  const changeCoverDialogState = usePopupState({
    variant: "popover",
    popupId: "change-cover-dialog",
  });

  const { mutate: updateTemplate, isPending: updatingTemplate } =
    useUpdateLibraryComponentTemplate(component.id);

  const handleCoverChange = React.useCallback(
    (value: string) => {
      changeCoverDialogState.close();
      if (
        value === componentImage ||
        (!componentImage && value === defaultLogo)
      ) {
        return;
      }

      updateTemplate({
        ...component,
        image: value,
      });
    },
    [changeCoverDialogState],
  );

  const handleUpdateIcon = (iconName: string) => {
    iconsMenuState.close();
    if (iconName === component.iconName) {
      return;
    }
    updateTemplate({
      ...component,
      iconName,
    });
  };

  const displayMenu =
    isPrivate ||
    (currentUser.isTeamAccount &&
      component.privacy === LibraryPrivacy.TEAM &&
      currentUser.teamRole === TeamRole.OWNER);

  return (
    <>
      <TableRow
        className={clsx(s.root, className)}
        hover
        onClick={onContentClick}
        onDoubleClick={onContentDoubleClick}
        data-template-id={component.id}
      >
        <TableCell
          component="th"
          scope="row"
          sx={{ width: columnWidths.thumbnail }}
        >
          <Box onClick={(e) => e.stopPropagation()}>
            <Box
              className={clsx(s.cover, JourneyAnchors.COMPONENT_TEMPLATE_IMAGE)}
              {...(component.coachId
                ? bindTrigger(changeCoverDialogState)
                : {})}
            />
          </Box>
        </TableCell>

        <TableCell
          component="th"
          scope="row"
          sx={{ width: columnWidths.title }}
        >
          <Box display="flex" alignItems="center">
            <Box onClick={(e) => e.stopPropagation()}>
              <ListItemIcon
                children={
                  <ComponentIcon
                    variant="icon"
                    componentData={{
                      iconName: component.iconName,
                      type: type,
                    }}
                    className={JourneyAnchors.COMPONENT_TEMPLATE_ICON}
                  />
                }
                className={s.icon}
                {...(component.coachId ? bindTrigger(iconsMenuState) : {})}
              />
            </Box>
            <ListItemText
              primary={component.title}
              primaryTypographyProps={{
                className: s.title,
              }}
            />
          </Box>
        </TableCell>

        <TableCell align="center" sx={{ width: columnWidths.type }}>
          <ComponentTemplateCategoryBadge category={component.type} />
        </TableCell>

        <TableCell align="center" sx={{ width: columnWidths.type }}>
          <ComponentTemplatePrivacyBadge
            template={component}
            showLabel={false}
          />
        </TableCell>

        <TableCell align="center" sx={{ width: columnWidths.actions }}>
          <Box
            onClick={(e) => e.stopPropagation()}
            display={"flex"}
            alignContent={"center"}
            flexDirection="column-reverse"
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Box
              onClick={onContentClick}
              onDoubleClick={onContentDoubleClick}
              data-template-id={component.id}
            >
              <IconButton children={<ArrowForwardRounded />} size="large" />
            </Box>
            {displayMenu && (
              <ComponentTemplateMenu
                component={component}
                onEdit={onEdit}
                isTeamAccount={currentUser.isTeamAccount}
                userTeamRole={currentUser.teamRole}
                userId={currentUser.id}
              />
            )}
          </Box>
        </TableCell>
        <Box onClick={(e) => e.stopPropagation()}>
          <SelectComponentIconsMenu
            {...bindMenu(iconsMenuState)}
            className={s.iconsMenu}
            anchorOrigin={{
              vertical: "center",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "center",
              horizontal: "left",
            }}
            onSubmit={(iconName) => {
              handleUpdateIcon(iconName);
            }}
            hideApplyToAll
            componentData={{
              type: component.type,
              iconName: component.iconName,
            }}
          />
        </Box>
        {changeCoverDialogState.isOpen && (
          <Box
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <ChangeComponentCoverDialog
              open
              isTemplateLibrary
              onClose={changeCoverDialogState.close}
              componentData={
                {
                  ...component,
                  image: componentImage || defaultLogo,
                } as any
              }
              onCoverChange={handleCoverChange}
              workspaceLogo={defaultLogo}
            />
          </Box>
        )}
      </TableRow>
    </>
  );
};

export default ComponentTemplateRow;
